/**
 * MasterComposer — "마스터 = 전부" 에서 **빼기 / 연결 on-off** 로 slice 를 구성하는 모달.
 *
 * PackPicker 와 **병렬 경로**:
 * - PackPicker: "시작점 단축키" — 팩 다중 선택으로 시작.
 * - MasterComposer: "마스터에서 빼기" — 마스터 블럭 전부(현재 111, BLOCKS_TUPLE.length SSOT)를 기본으로 두고 불필요한 블럭/shape 제외.
 *
 * UX 구조 (탭 2개):
 * 1) Blocks 탭 — 마스터 블럭 카드 그리드 + 카테고리 필터 + 검색 + 체크박스
 *    - 체크 해제 시 `excludedBlockIds` 에 추가 (제외)
 * 2) Connections 탭 — 선택된 블럭 간 가능한 shape 매트릭스 (source × relation × target 카드 리스트)
 *    - 체크 해제 시 `excludedShapes` 에 추가 (자연키)
 *
 * 사이드 요약:
 * - 포함 블럭 수 / 제외 블럭 수 / 제외 shape 수
 * - "프리뷰" 버튼: 최종 적용될 slice 의 kinds/relations/shapes 카운트
 * - "적용" 버튼: OntologyEditor 의 draft 를 이 slice 로 reset
 */
import { useEffect, useMemo, useState } from "react"
import type {
	AdminClient,
	BlockCategory,
	KindDef,
	OntologyBlock,
	OntologyPreset,
	OntologySlice,
	ShapeDef
} from "../../api/admin-client.ts"

type TabKey = "blocks" | "connections"

interface MasterComposerProps {
	/**
	 * 마스터 preset — 마스터 블럭 전부(현재 111) + ADDITIONAL_MASTER_SHAPES 를 포함한 시작 slice.
	 * 본 모달이 시작하는 기준점. 없으면 로딩 상태.
	 */
	readonly masterPreset: OntologyPreset | undefined
	/** 마스터 블럭 카탈로그 — 카드 그리드 원천. */
	readonly blocks: ReadonlyArray<OntologyBlock>
	/**
	 * 마스터 shape 풀 — 서버 `/admin/ontology/shapes` 응답. Connections 탭 매트릭스 원천.
	 * 없으면 fallback 으로 `masterPreset.slice.shapes` 를 사용.
	 */
	readonly masterShapes: ReadonlyArray<ShapeDef>
	readonly loading?: boolean
	/** 기존 draft 에서 이미 제외된 블럭 id / shape 자연키 (초기 체크 해제 상태 복원) */
	readonly initialExcludedBlockIds?: ReadonlyArray<string>
	readonly initialExcludedShapes?: ReadonlyArray<string>
	readonly hasUnsavedChanges: boolean
	readonly onClose: () => void
	/**
	 * 최종 slice 적용. OntologyEditor 가 받아 `useOntologyDraft.reset(slice)` 로 반영.
	 * slice 는 제외가 이미 적용된 결과 — `excludedBlockIds` / `excludedShapes` 메타도 포함한다.
	 */
	readonly onApply: (slice: OntologySlice) => void
}

/** 카테고리 탭 라벨 순서 — BlocksPage 및 PackPicker 와 일치. */
const CATEGORY_ORDER: ReadonlyArray<{ readonly key: BlockCategory | "all"; readonly label: string }> = [
	{ key: "all", label: "전체" },
	{ key: "actor", label: "행위자" },
	{ key: "content", label: "콘텐츠" },
	{ key: "event", label: "이벤트" },
	{ key: "product", label: "제품" },
	{ key: "place", label: "장소" },
	{ key: "structure", label: "구조" },
	{ key: "decision", label: "결정" }
]

/** shape 자연키 — `${source}|${relation}|${target}`. */
function shapeKey(s: ShapeDef): string {
	return `${s.sourceKind}|${s.relation}|${s.targetKind}`
}

export function MasterComposer(props: MasterComposerProps) {
	const { masterPreset, blocks, masterShapes } = props

	const [tab, setTab] = useState<TabKey>("blocks")
	const [category, setCategory] = useState<BlockCategory | "all">("all")
	const [query, setQuery] = useState("")

	// 제외 상태 — Set 으로 관리해 toggle 이 O(1).
	const [excludedBlockIds, setExcludedBlockIds] = useState<ReadonlySet<string>>(
		() => new Set(props.initialExcludedBlockIds ?? [])
	)
	const [excludedShapes, setExcludedShapes] = useState<ReadonlySet<string>>(
		() => new Set(props.initialExcludedShapes ?? [])
	)

	// 동일 props 로 모달을 재오픈할 때 초기 상태 리셋
	useEffect(() => {
		setExcludedBlockIds(new Set(props.initialExcludedBlockIds ?? []))
		setExcludedShapes(new Set(props.initialExcludedShapes ?? []))
	}, [props.initialExcludedBlockIds, props.initialExcludedShapes])

	// 블럭 id → 단일 kind 이름 매핑 (1 블럭 = 1 kind 엄격 1:1)
	const blockIdToKind = useMemo(() => {
		const map = new Map<string, string>()
		for (const b of blocks) {
			const kind = b.slice.kinds[0]
			if (kind) map.set(b.id, kind.name)
		}
		return map
	}, [blocks])

	// kind 이름 → 블럭 매핑 (카드/매트릭스의 역조회)
	const kindToBlock = useMemo(() => {
		const map = new Map<string, OntologyBlock>()
		for (const b of blocks) {
			const kind = b.slice.kinds[0]
			if (kind) map.set(kind.name, b)
		}
		return map
	}, [blocks])

	// 제외된 블럭 id → 제외된 kind 이름 집합
	const excludedKindNames = useMemo(() => {
		const set = new Set<string>()
		for (const id of excludedBlockIds) {
			const kind = blockIdToKind.get(id)
			if (kind) set.add(kind)
		}
		return set
	}, [excludedBlockIds, blockIdToKind])

	// 현재 탭 내 필터링된 블럭 목록 (카테고리 + 검색)
	const visibleBlocks = useMemo(() => {
		const q = query.trim().toLowerCase()
		return blocks.filter((b) => {
			if (category !== "all" && b.category !== category) return false
			if (q === "") return true
			return (
				b.id.toLowerCase().includes(q) ||
				b.displayName.toLowerCase().includes(q) ||
				b.slice.kinds.some((k) => k.name.includes(q))
			)
		})
	}, [blocks, category, query])

	// Connections 탭: 포함된 shape 만 (제외 블럭 참조 자동 제거)
	const availableShapes = useMemo(() => {
		// masterShapes 가 비면 masterPreset.slice.shapes 로 fallback.
		const pool: ReadonlyArray<ShapeDef> =
			masterShapes.length > 0 ? masterShapes : (masterPreset?.slice.shapes ?? [])
		return pool.filter((s) => !excludedKindNames.has(s.sourceKind) && !excludedKindNames.has(s.targetKind))
	}, [masterShapes, masterPreset, excludedKindNames])

	// Connections 탭: 필터링된 shape 그룹 — sourceKind 로 그룹화해 매트릭스 느낌 내기
	const groupedByRelation = useMemo(() => {
		const groups = new Map<string, ShapeDef[]>()
		for (const s of availableShapes) {
			const arr = groups.get(s.relation) ?? []
			arr.push(s)
			groups.set(s.relation, arr)
		}
		return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]))
	}, [availableShapes])

	// 블럭 카운트 요약
	const totalBlocks = blocks.length
	const excludedBlocksCount = excludedBlockIds.size
	const includedBlocksCount = totalBlocks - excludedBlocksCount

	// shape 카운트 요약 — 제외된 shape 수 / 최종 shape 수
	const totalMasterShapes = masterShapes.length
	const excludedShapesCount = excludedShapes.size
	const finalShapesCount = availableShapes.length - excludedShapesCount

	function toggleBlock(id: string) {
		setExcludedBlockIds((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	function toggleShape(key: string) {
		setExcludedShapes((prev) => {
			const next = new Set(prev)
			if (next.has(key)) next.delete(key)
			else next.add(key)
			return next
		})
	}

	function clearAllExclusions() {
		setExcludedBlockIds(new Set())
		setExcludedShapes(new Set())
	}

	// 최종 slice 계산 — 제외 적용 후 kinds / shapes 를 필터링.
	function buildFinalSlice(): OntologySlice | undefined {
		if (!masterPreset) return undefined
		const base = masterPreset.slice
		const nextKinds = base.kinds.filter((k) => !excludedKindNames.has(k.name))
		const nextShapes = (
			masterShapes.length > 0
				? (masterShapes as ReadonlyArray<ShapeDef>)
				: (base.shapes as ReadonlyArray<ShapeDef>)
		).filter((s) => {
			if (excludedKindNames.has(s.sourceKind) || excludedKindNames.has(s.targetKind)) return false
			if (excludedShapes.has(shapeKey(s))) return false
			return true
		})

		return {
			...base,
			kinds: nextKinds as ReadonlyArray<KindDef>,
			shapes: nextShapes,
			...(excludedBlockIds.size > 0 ? { excludedBlockIds: [...excludedBlockIds] } : {}),
			...(excludedShapes.size > 0 ? { excludedShapes: [...excludedShapes] } : {})
		}
	}

	const canApply = !props.loading && masterPreset !== undefined

	return (
		<dialog
			className="modal-backdrop"
			open
			onMouseDown={(e) => e.currentTarget === e.target && props.onClose()}
		>
			<div
				className="modal pack-picker-modal"
				onMouseDown={(e) => e.stopPropagation()}
				style={{ maxWidth: 1100 }}
			>
				<div className="modal-head">
					<h2>마스터 컴포저</h2>
					<p>
						{blocks.length > 0 ? `${blocks.length}개 블럭` : "전체 블럭"} 전부로 시작. 불필요한 블럭을 체크
						해제해 빼고, Connections 탭에서 개별 연결을 on/off 해 서비스 맞춤 slice 를 구성합니다.
					</p>
				</div>

				<div className="modal-body">
					{props.hasUnsavedChanges && (
						<div className="alert alert-warn" style={{ marginBottom: 14 }}>
							현재 저장되지 않은 변경사항이 있습니다. 적용하면 덮어써집니다.
						</div>
					)}

					{props.loading ? (
						<p className="muted">마스터 카탈로그를 불러오는 중…</p>
					) : !masterPreset ? (
						<p className="muted">마스터 preset 을 찾을 수 없습니다 (pion-master 누락).</p>
					) : (
						<div className="pack-picker-layout">
							<div className="pack-picker-main">
								<div className="pack-group-tabs" role="tablist" style={{ marginBottom: 12 }}>
									<button
										type="button"
										role="tab"
										aria-selected={tab === "blocks"}
										className={`pack-group-tab${tab === "blocks" ? " is-active" : ""}`}
										onClick={() => setTab("blocks")}
									>
										블럭
										<span className="pack-group-tab-count">{includedBlocksCount}</span>
									</button>
									<button
										type="button"
										role="tab"
										aria-selected={tab === "connections"}
										className={`pack-group-tab${tab === "connections" ? " is-active" : ""}`}
										onClick={() => setTab("connections")}
									>
										연결
										<span className="pack-group-tab-count">{finalShapesCount}</span>
									</button>
								</div>

								{tab === "blocks" ? (
									<>
										<div className="pack-group-tabs" role="tablist" style={{ marginBottom: 10 }}>
											{CATEGORY_ORDER.map((c) => {
												const count =
													c.key === "all" ? blocks.length : blocks.filter((b) => b.category === c.key).length
												return (
													<button
														type="button"
														key={c.key}
														role="tab"
														aria-selected={category === c.key}
														className={`pack-group-tab${category === c.key ? " is-active" : ""}`}
														onClick={() => setCategory(c.key)}
													>
														{c.label}
														<span className="pack-group-tab-count">{count}</span>
													</button>
												)
											})}
										</div>

										<label className="field">
											<input
												className="input"
												type="search"
												placeholder="블럭 검색 (id / displayName / kind)"
												value={query}
												onChange={(e) => setQuery(e.target.value)}
											/>
										</label>

										<div className="pack-grid">
											{visibleBlocks.length === 0 ? (
												<p className="muted">일치하는 블럭이 없습니다.</p>
											) : (
												visibleBlocks.map((b) => {
													const isIncluded = !excludedBlockIds.has(b.id)
													return (
														<label key={b.id} className={`pack-card${isIncluded ? " is-selected" : ""}`}>
															<div className="pack-card-head">
																<input
																	type="checkbox"
																	checked={isIncluded}
																	onChange={() => toggleBlock(b.id)}
																/>
																<span className="pack-card-icon" aria-hidden>
																	{b.icon || "◎"}
																</span>
																<strong>{b.displayName}</strong>
																<span className="pack-card-block-count" title={`category: ${b.category}`}>
																	{b.category}
																</span>
															</div>
															<p className="pack-card-desc">{b.description}</p>
															<div className="pack-card-stats">
																<span>kind {b.slice.kinds[0]?.name ?? "-"}</span>
																<span>relations {b.slice.relations.length}</span>
																<span>shapes {b.slice.shapes.length}</span>
															</div>
														</label>
													)
												})
											)}
										</div>
									</>
								) : (
									<>
										<p className="muted" style={{ marginBottom: 10 }}>
											제외된 블럭을 참조하는 연결은 자동으로 숨김 처리됩니다. 각 카드의 체크박스로 세부 연결을
											켜고 끌 수 있습니다.
										</p>
										<div className="pack-grid">
											{groupedByRelation.length === 0 ? (
												<p className="muted">가능한 연결이 없습니다.</p>
											) : (
												groupedByRelation.map(([relation, shapes]) => (
													<div key={relation} className="pack-card" style={{ cursor: "default" }}>
														<div className="pack-card-head">
															<strong>{relation}</strong>
															<span className="pack-card-block-count">{shapes.length}개 shape</span>
														</div>
														<div className="pack-card-blocks" style={{ flexDirection: "column" }}>
															{shapes.map((s) => {
																const key = shapeKey(s)
																const isOn = !excludedShapes.has(key)
																const src = kindToBlock.get(s.sourceKind)
																const tgt = kindToBlock.get(s.targetKind)
																return (
																	<label
																		key={key}
																		className="chip pack-card-block-chip"
																		style={{
																			gap: 6,
																			justifyContent: "flex-start",
																			opacity: isOn ? 1 : 0.5
																		}}
																	>
																		<input type="checkbox" checked={isOn} onChange={() => toggleShape(key)} />
																		<span aria-hidden>{src?.icon ?? "▢"}</span>
																		<span>{src?.displayName ?? s.sourceKind}</span>
																		<span style={{ opacity: 0.55 }}>→</span>
																		<span aria-hidden>{tgt?.icon ?? "▢"}</span>
																		<span>{tgt?.displayName ?? s.targetKind}</span>
																	</label>
																)
															})}
														</div>
													</div>
												))
											)}
										</div>
									</>
								)}
							</div>

							<aside className="pack-preview-side" aria-label="마스터 요약">
								<div className="pack-preview-head">
									<span className="pack-preview-kicker">마스터</span>
									<strong>파이오니어 마스터</strong>
								</div>
								<ul className="pack-preview-stats">
									<li>
										<span className="pack-preview-stat-label">포함 블럭</span>
										<span className="pack-preview-stat-value">{includedBlocksCount}</span>
									</li>
									<li>
										<span className="pack-preview-stat-label">제외 블럭</span>
										<span className="pack-preview-stat-value">{excludedBlocksCount}</span>
									</li>
									<li>
										<span className="pack-preview-stat-label">마스터 shape</span>
										<span className="pack-preview-stat-value">{totalMasterShapes}</span>
									</li>
									<li>
										<span className="pack-preview-stat-label">제외 shape</span>
										<span className="pack-preview-stat-value">{excludedShapesCount}</span>
									</li>
									<li>
										<span className="pack-preview-stat-label">최종 shape</span>
										<span className="pack-preview-stat-value">{finalShapesCount}</span>
									</li>
								</ul>

								{(excludedBlocksCount > 0 || excludedShapesCount > 0) && (
									<button
										type="button"
										className="btn btn-ghost btn-sm"
										onClick={clearAllExclusions}
										style={{ marginTop: 10 }}
									>
										모든 제외 해제
									</button>
								)}
							</aside>
						</div>
					)}
				</div>

				<div className="modal-foot">
					<button type="button" className="btn btn-ghost" onClick={props.onClose}>
						취소
					</button>
					<button
						type="button"
						className="btn btn-primary"
						disabled={!canApply}
						onClick={() => {
							const slice = buildFinalSlice()
							if (slice) props.onApply(slice)
						}}
					>
						마스터 슬라이스 적용
					</button>
				</div>
			</div>
		</dialog>
	)
}

/**
 * MasterComposer 데이터 페치 헬퍼 — AdminClient 에서 masterPreset / blocks / masterShapes 를
 * 한 번에 로드. OntologyEditor 가 모달 열기 전에 호출.
 *
 * masterPreset 은 `/admin/ontology/presets` 응답에서 `id === "pion-master"` 를 고른다.
 */
export async function loadMasterComposerData(client: AdminClient): Promise<{
	readonly masterPreset: OntologyPreset | undefined
	readonly blocks: ReadonlyArray<OntologyBlock>
	readonly masterShapes: ReadonlyArray<ShapeDef>
}> {
	const [presets, blocks, masterShapes] = await Promise.all([
		client.getOntologyPresets(),
		client.getOntologyBlocks(),
		client.getMasterShapes()
	])
	const masterPreset = presets.find((p) => p.id === "pion-master")
	return { masterPreset, blocks, masterShapes }
}
